<template>
  <web-app-layout :showLogo="true" :noPadding="true" :embedded="isEmbedded">
    <div v-if="!isAuthenticated" class="auth-redirect">
      <SmartBanner />
    </div>

    <div class="profile-container" :class="{ embedded: isEmbedded }">
      <div v-if="state.error" class="error-message">
        {{ state.error }}
      </div>

      <!-- Global Loading Indicator -->
      <div v-if="initialLoading" class="loading-container initial-loading">
        <el-icon :size="60" class="loading-spinner">
          <Loading />
        </el-icon>
        <p class="mt-3">Finding your organization profile...</p>
      </div>

      <template v-else>
        <div class="profile-content-container">
          <profile-page-skeleton v-if="loading" />

          <template v-else-if="state.organization">
            <!-- Embedded view - Simple avatar and title -->
            <div v-if="isEmbedded" class="embedded-profile-header">
              <div class="embedded-profile-avatar">
                <img
                  :src="state.organization?.avatar || defaultAvatar"
                  alt="Organization Avatar"
                  @error="handleAvatarError"
                />
              </div>
              <div class="embedded-profile-name-section">
                <h1 class="embedded-user-name">{{ state.organization.name }}</h1>
                <p class="embedded-user-title" v-if="state.organization.organization_type">
                  {{ formatOrgType }}
                </p>
              </div>
            </div>

            <!-- Normal view - Background with Avatar and Name on top -->
            <div
              v-else
              class="profile-header"
              :style="{
                backgroundImage: `url(${state.organization?.background || defaultBackground})`,
              }"
            >
              <!-- Dark overlay for better text visibility -->
              <div class="profile-header-overlay" />

              <div class="profile-content-overlay">
                <div class="profile-avatar">
                  <img
                    :src="state.organization?.avatar || defaultAvatar"
                    alt="Organization Avatar"
                    @error="handleAvatarError"
                  />
                </div>
                <div class="profile-name-section">
                  <h1 class="user-name">{{ state.organization.name }}</h1>
                  <p class="user-title" v-if="state.organization.organization_type">
                    {{ formatOrgType }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Embedded view - Show shop content directly if there are stickers -->
            <div
              v-if="isEmbedded && hasStickers"
              class="embedded-shop-content"
            >
              <div class="profile-info-section">
                <div class="stickers-section">
                  <h3>Organization Stickers</h3>
                  <div class="stickers-container">
                    <div v-if="stickerSearchLoading" class="sticker-search-loading">
                      <el-icon :size="30" class="loading-spinner">
                        <Loading />
                      </el-icon>
                      <p>Searching stickers...</p>
                    </div>
                    <div
                      v-else-if="(!state.stickers || state.stickers.length === 0) && searchString"
                      class="no-search-results"
                    >
                      <p>No stickers found for "{{ searchString }}"</p>
                    </div>
                    <stickers-grid-template v-else :stickers="state.stickers || []" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Normal view - Tabs Section - Only show if there are stickers -->
            <div v-else-if="hasStickers" class="tabs-container">
              <div class="tab-buttons">
                <div class="tab-navigation">
                  <button
                    type="button"
                    :class="['tab-button', { active: activeTab === 'about' }]"
                    @click="activeTab = 'about'"
                  >
                    ABOUT
                  </button>
                  <button
                    type="button"
                    :class="['tab-button', { active: activeTab === 'shop' }]"
                    @click="activeTab = 'shop'"
                  >
                    SHOP
                  </button>
                </div>
                <div v-if="activeTab === 'shop'" class="search-container">
                  <el-input
                    v-model="searchString"
                    placeholder="Search stickers..."
                    class="sticker-search"
                    @input="handleStickerSearch"
                    clearable
                  >
                    <template #prefix>
                      <el-icon><Search /></el-icon>
                    </template>
                  </el-input>
                </div>
              </div>

              <!-- About Tab Content -->
              <div v-if="activeTab === 'about'" class="tab-content">
                <div class="profile-info-section">
                  <div class="bio-section" v-if="state.organization?.purpose">
                    <h2 class="purpose-title">{{ state.organization.purpose }}</h2>
                  </div>

                  <div class="bio-section" v-if="state.organization?.about">
                    <p>{{ state.organization.about }}</p>
                  </div>

                  <!-- Values Section -->
                  <div class="values-section" v-if="state.organization?.values?.length">
                    <h3>VALUES</h3>
                    <div class="tags-container">
                      <el-tag
                        v-for="value in state.organization.values"
                        :key="value.id"
                        class="value-tag"
                      >
                        {{ value.value }}
                      </el-tag>
                    </div>
                  </div>

                  <!-- Bullhorns Section -->
                  <div class="values-section" v-if="state.organization?.bullhorns?.length">
                    <h3>CAUSES</h3>
                    <div class="tags-container">
                      <el-tag
                        v-for="bullhorn in state.organization.bullhorns"
                        :key="bullhorn.id"
                        class="interest-tag"
                      >
                        {{ bullhorn.bullhorn }}
                      </el-tag>
                    </div>
                  </div>

                  <!-- Channels Section -->
                  <div class="values-section" v-if="state.organization?.channels?.length">
                    <h3>CHANNELS</h3>
                    <div class="tags-container">
                      <el-tag
                        v-for="channel in state.organization.channels"
                        :key="channel.id"
                        class="channel-tag"
                      >
                        {{ channel.channel }}
                      </el-tag>
                    </div>
                  </div>

                  <!-- Contact Information -->
                  <div
                    class="contact-section"
                    v-if="state.organization.email || state.organization.einNumber"
                  >
                    <h3>CONTACT INFORMATION</h3>
                    <div class="contact-info">
                      <div class="info-item" v-if="state.organization?.email">
                        <i class="el-icon-message" />
                        <span>{{ state.organization.email }}</span>
                      </div>
                      <div class="info-item" v-if="state.organization?.einNumber">
                        <i class="el-icon-document" />
                        <span>EIN: {{ state.organization.einNumber }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Shop Tab Content -->
              <div v-if="activeTab === 'shop'" class="tab-content">
                <div class="profile-info-section">
                  <div class="stickers-section">
                    <h3>Organization Stickers</h3>
                    <div class="stickers-container">
                      <div v-if="stickerSearchLoading" class="sticker-search-loading">
                        <el-icon :size="30" class="loading-spinner">
                          <Loading />
                        </el-icon>
                        <p>Searching stickers...</p>
                      </div>
                      <div
                        v-else-if="(!state.stickers || state.stickers.length === 0) && searchString"
                        class="no-search-results"
                      >
                        <p>No stickers found for "{{ searchString }}"</p>
                      </div>
                      <stickers-grid-template v-else :stickers="state.stickers || []" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- No Tabs - Show About content directly when no stickers -->
            <div v-else class="profile-info-section">
              <div class="bio-section" v-if="state.organization?.purpose">
                <h2 class="purpose-title">{{ state.organization.purpose }}</h2>
              </div>

              <div class="bio-section" v-if="state.organization?.about">
                <p>{{ state.organization.about }}</p>
              </div>

              <!-- Values Section -->
              <div class="values-section" v-if="state.organization?.values?.length">
                <h3>VALUES</h3>
                <div class="tags-container">
                  <el-tag
                    v-for="value in state.organization.values"
                    :key="value.id"
                    class="value-tag"
                  >
                    {{ value.value }}
                  </el-tag>
                </div>
              </div>

              <!-- Bullhorns Section -->
              <div class="values-section" v-if="state.organization?.bullhorns?.length">
                <h3>CAUSES</h3>
                <div class="tags-container">
                  <el-tag
                    v-for="bullhorn in state.organization.bullhorns"
                    :key="bullhorn.id"
                    class="interest-tag"
                  >
                    {{ bullhorn.bullhorn }}
                  </el-tag>
                </div>
              </div>

              <!-- Channels Section -->
              <div class="values-section" v-if="state.organization?.channels?.length">
                <h3>CHANNELS</h3>
                <div class="tags-container">
                  <el-tag
                    v-for="channel in state.organization.channels"
                    :key="channel.id"
                    class="channel-tag"
                  >
                    {{ channel.channel }}
                  </el-tag>
                </div>
              </div>

              <!-- Contact Information -->
              <div
                class="contact-section"
                v-if="state.organization.email || state.organization.einNumber"
              >
                <h3>CONTACT INFORMATION</h3>
                <div class="contact-info">
                  <div class="info-item" v-if="state.organization?.email">
                    <i class="el-icon-message" />
                    <span>{{ state.organization.email }}</span>
                  </div>
                  <div class="info-item" v-if="state.organization?.einNumber">
                    <i class="el-icon-document" />
                    <span>EIN: {{ state.organization.einNumber }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
  </web-app-layout>
</template>

<script lang="ts">
import {
  defineComponent, onMounted, reactive, computed, watch, ref,
} from 'vue';
import { Search } from '@element-plus/icons-vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import WebAppLayout from '@/views/web-app/dashboard/WebAppLayout.vue';
import ProfilePageSkeleton from '@/views/web-app/components/ProfilePageSkeleton.vue';
import gqlRequest from '@/views/web-app/dashboard/gql-request';
import defaultAvatarImg from '@/assets/images/default-profile.jpeg';
import defaultBackgroundImg from '@/assets/images/bg.jpg';
import SmartBanner from '@/views/web-app/sticker/SmartBanner.vue';
import StickersGridTemplate from '@/views/web-app/sticker/StickersGridTemplate.vue';
import { isEmbeddedContext, isEmbeddedRoute } from '@/utils/embeddedUtils';

interface OrganizationData {
  id?: string;
  name?: string;
  avatar?: string;
  background?: string;
  about?: string;
  purpose?: string;
  email?: string;
  einNumber?: string;
  organization_type?: string;
  profit_type?: string;
  nonprofit_type?: string;
  dob?: string;
  onboarded?: boolean;
  active?: boolean;
  approved?: boolean;
  values?: Array<{
    id: string;
    value: string;
  }>;
  bullhorns?: Array<{
    id: string;
    bullhorn: string;
  }>;
  channels?: Array<{
    id: string;
    channel: string;
  }>;
  stickers?: Array<{
    id: string;
    name: string;
    imageUrl: string;
  }>;
}

export default defineComponent({
  name: 'OrganizationProfile',
  components: {
    WebAppLayout,
    ProfilePageSkeleton,
    SmartBanner,
    StickersGridTemplate,
    Search,
  },
  setup() {
    const {
      loading,
      fetchOrganization,
      fetchAllOrganizations,
      fetchPublicOrganization,
      fetchPublicOrganizationBySlug,
      fetchApprovedOrgStickers,
      fetchApprovedOrgStickersBySlug,
    } = gqlRequest();
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const state = reactive({
      organization: null as OrganizationData | null,
      error: null as string | null,
      stickers: null as Array<{
        id: string;
        name: string;
        imageUrl: string;
      }> | null,
    });

    const organizations = ref<{ id: string, name: string, createdAt?: string }[]>([]);
    const filteredOrganizations = ref<{ id: string, name: string }[]>([]);
    const selectedOrgId = ref('');
    const searchLoading = ref(false);
    const currentPage = ref(1);
    const hasMorePages = ref(true);
    const isLoadingMore = ref(false);
    const lastSearchQuery = ref('');
    const initialLoading = ref(true);
    const activeTab = ref('about');
    const searchString = ref('');
    const searchDebounceTimer = ref<number | null>(null);
    const stickerSearchLoading = ref(false);
    const hasStickers = ref(false);
    const dropdownScrollListener = ref<(() => void) | null>(null);

    const defaultAvatar = defaultAvatarImg;
    const defaultBackground = defaultBackgroundImg;

    const orgId = computed(() => route.params.id as string);
    const orgSlug = computed(() => route.params.slug as string);
    const isSlugRoute = computed(() => !!orgSlug.value);

    const isAuthenticated = computed(() => store.getters['users/isMeSignedIn']);

    // Check if we're in embedded context
    const isEmbedded = computed(() => isEmbeddedRoute(route.path) || isEmbeddedContext());

    // Check for tab query parameter
    const tabQueryParam = computed(() => route.query.tab as string);

    const formatOrgType = computed(() => {
      if (!state.organization?.organization_type) return '';

      const type = state.organization.organization_type;

      return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    });

    // Function to set initial active tab based on query parameter
    function setInitialActiveTab() {
      if (tabQueryParam.value === 'shop' && hasStickers.value) {
        activeTab.value = 'shop';
      } else {
        activeTab.value = 'about';
      }
    }

    function selectMostRecentOrganization() {
      if (!organizations.value.length) {
        return;
      }

      const firstOrg = organizations.value[0];
      if (firstOrg && firstOrg.id) {
        selectedOrgId.value = firstOrg.id;

        router.push(`/web/profile/organization/${firstOrg.id}`);
      }
    }

    function handleOrgChange(id: string) {
      if (!id) {
        if (organizations.value.length > 0) {
          selectMostRecentOrganization();
        }
        return;
      }

      if (id && id !== orgId.value) {
        router.push(`/web/profile/organization/${id}`);
      }
    }

    async function searchOrganizations(query: string) {
      if (searchLoading.value) return;

      lastSearchQuery.value = query || '';
      searchLoading.value = true;

      try {
        // Reset pagination for new search
        currentPage.value = 1;
        hasMorePages.value = true;

        if (!query || query.length === 0) {
          // Load initial organizations without search
          const orgs = await fetchAllOrganizations(50, 0, '');
          if (orgs && orgs.length) {
            organizations.value = orgs;
            filteredOrganizations.value = orgs;
            hasMorePages.value = orgs.length === 50;
          }
        } else if (query.length >= 2) {
          // Search with query
          const orgs = await fetchAllOrganizations(50, 0, query);
          if (orgs && orgs.length) {
            organizations.value = orgs;
            filteredOrganizations.value = orgs;
            hasMorePages.value = orgs.length === 50;
          } else {
            organizations.value = [];
            filteredOrganizations.value = [];
            hasMorePages.value = false;
          }
        }
      } catch (error) {
        console.error('Error searching organizations:', error);
        organizations.value = [];
        filteredOrganizations.value = [];
        hasMorePages.value = false;
      } finally {
        searchLoading.value = false;
      }
    }

    async function loadMoreOrganizations() {
      if (isLoadingMore.value || !hasMorePages.value || lastSearchQuery.value) {
        return;
      }

      try {
        isLoadingMore.value = true;
        currentPage.value += 1;

        const skip = (currentPage.value - 1) * 50;
        const orgs = await fetchAllOrganizations(50, skip, lastSearchQuery.value);

        if (orgs && orgs.length) {
          // Append new organizations, avoiding duplicates
          const existingIds = new Set(organizations.value.map((org) => org.id));
          const newOrgs = orgs.filter((org) => !existingIds.has(org.id));

          organizations.value.push(...newOrgs);
          filteredOrganizations.value = [...organizations.value];
          hasMorePages.value = orgs.length === 50;
        } else {
          hasMorePages.value = false;
        }
      } catch (error) {
        console.error('Error loading more organizations:', error);
        hasMorePages.value = false;
      } finally {
        isLoadingMore.value = false;
      }
    }

    function handleDropdownVisibleChange(visible: boolean) {
      if (visible) {
        // Add scroll listener when dropdown opens
        setTimeout(() => {
          const dropdown = document.querySelector('.org-select-dropdown .el-select-dropdown__wrap');
          if (dropdown && !dropdownScrollListener.value) {
            dropdownScrollListener.value = () => {
              const { scrollTop, scrollHeight, clientHeight } = dropdown;
              // Load more when scrolled to bottom (with 50px threshold)
              const shouldLoadMore = scrollTop + clientHeight >= scrollHeight - 50
                && hasMorePages.value && !lastSearchQuery.value;
              if (shouldLoadMore) {
                loadMoreOrganizations();
              }
            };
            dropdown.addEventListener('scroll', dropdownScrollListener.value);
          }
        }, 100);
      } else {
        // Remove scroll listener when dropdown closes
        const dropdown = document.querySelector('.org-select-dropdown .el-select-dropdown__wrap');
        if (dropdown && dropdownScrollListener.value) {
          dropdown.removeEventListener('scroll', dropdownScrollListener.value);
          dropdownScrollListener.value = null;
        }
      }
    }

    async function fetchOrganizationStickers(organizationId: string, search?: string) {
      try {
        let stickers;

        if (isSlugRoute.value) {
          stickers = await fetchApprovedOrgStickersBySlug(orgSlug.value, search);
        } else {
          stickers = await fetchApprovedOrgStickers(organizationId, search);
        }

        if (stickers && stickers.length > 0) {
          state.stickers = stickers;
          // Only set hasStickers to true if this is the initial load (no search query)
          if (!search) {
            hasStickers.value = true;
          }
        } else {
          state.stickers = [];
          // Only set hasStickers to false if this is the initial load (no search query)
          if (!search) {
            hasStickers.value = false;
          }
        }
      } catch (error) {
        console.error('Error fetching organization stickers:', error);
        state.stickers = [];
        stickerSearchLoading.value = false;
        // Only set hasStickers to false if this is the initial load (no search query)
        if (!search) {
          hasStickers.value = false;
        }
      }
    }

    function handleStickerSearch(query: string) {
      searchString.value = query;

      // Clear existing timer
      if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value);
      }

      // Handle empty search immediately
      if (!query.trim()) {
        stickerSearchLoading.value = false;
        const identifier = orgId.value || orgSlug.value;
        if (identifier) {
          fetchOrganizationStickers(identifier); // This will reload all stickers
        }
        return;
      }

      // Set loading state for non-empty search
      stickerSearchLoading.value = true;

      // Set new debounced search
      searchDebounceTimer.value = setTimeout(async () => {
        const identifier = orgId.value || orgSlug.value;
        if (identifier) {
          await fetchOrganizationStickers(identifier, query.trim());
        }
        stickerSearchLoading.value = false;
      }, 300) as any;
    }

    async function fetchOrganizationProfile() {
      const identifier = orgId.value || orgSlug.value;
      if (!identifier) {
        return;
      }

      try {
        loading.value = true;
        state.error = null;
        hasStickers.value = false; // Reset stickers state when fetching new organization
        let organization = null;

        if (isSlugRoute.value) {
          organization = await fetchPublicOrganizationBySlug(orgSlug.value);
        } else {
          organization = await fetchPublicOrganization(orgId.value);
        }

        if (organization) {
          state.organization = organization;
          initialLoading.value = false;

          await fetchOrganizationStickers(identifier);
          setInitialActiveTab(); // Set active tab after fetching stickers
        } else {
          state.error = 'Organization not found';
          console.error('Organization not found for identifier:', identifier);
        }
      } catch (error) {
        console.error('Error loading organization profile:', error);
        state.error = 'Failed to load organization data';
        if (error.message?.includes('not authorized')) {
          const currentPath = route.fullPath;
          router.push(`/auth/web/signin?redirect=${encodeURIComponent(currentPath)}`);
        }
      } finally {
        loading.value = false;
        initialLoading.value = false;
      }
    }

    async function loadOrganizations() {
      if (!isAuthenticated.value) return;

      try {
        searchLoading.value = true;
        initialLoading.value = true;
        currentPage.value = 1;
        hasMorePages.value = true;
        const orgs = await fetchAllOrganizations(50, 0, '');

        if (orgs && orgs.length) {
          organizations.value = orgs;
          filteredOrganizations.value = orgs;
          hasMorePages.value = orgs.length === 50;

          if (orgId.value) {
            selectedOrgId.value = orgId.value;
          } else {
            selectMostRecentOrganization();
          }
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        searchLoading.value = false;
        setTimeout(() => {
          initialLoading.value = false;
        }, 300);
      }
    }

    onMounted(async () => {
      if (isAuthenticated.value && !isSlugRoute.value) {
        await loadOrganizations();
      }

      const identifier = orgId.value || orgSlug.value;
      if (identifier) {
        fetchOrganizationProfile();
      }
    });

    watch(() => route.params.id, (newId) => {
      selectedOrgId.value = newId as string;

      if (newId) {
        fetchOrganizationProfile();
      } else if (organizations.value.length > 0 && !isSlugRoute.value) {
        selectMostRecentOrganization();
      }
    });

    watch(() => route.params.slug, (newSlug) => {
      if (newSlug) {
        fetchOrganizationProfile();
      }
    });

    watch(() => route.name, () => {
      const identifier = orgId.value || orgSlug.value;
      if (identifier) {
        fetchOrganizationProfile();
      }
    });

    // Watch for tab query parameter changes
    watch(() => route.query.tab, () => {
      setInitialActiveTab();
    });

    const handleAvatarError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      if (!target) return;
      
      // Prevent infinite loop if default image also fails
      if (target.src === defaultAvatar) return;
      
      target.src = defaultAvatar;
    };

    return {
      state,
      loading,
      searchLoading,
      initialLoading,
      stickerSearchLoading,
      defaultAvatar,
      defaultBackground,
      isAuthenticated,
      isEmbedded,
      formatOrgType,
      organizations,
      filteredOrganizations,
      selectedOrgId,
      orgSlug,
      isSlugRoute,
      activeTab,
      searchString,
      hasStickers,
      hasMorePages,
      isLoadingMore,
      lastSearchQuery,
      handleOrgChange,
      searchOrganizations,
      handleStickerSearch,
      handleDropdownVisibleChange,
      loadMoreOrganizations,
      handleAvatarError,
    };
  },
});
</script>

<style lang="scss">
/* Global styles for Element Plus components */
.org-select-dropdown {
  .el-select-dropdown__item {
    padding-left: 0 !important;
  }

  .load-more-option {
    text-align: center !important;
    font-style: italic !important;
    color: #64748b !important;
    background-color: #f8fafc !important;
    border-top: 1px solid #e2e8f0 !important;

    &:hover {
      background-color: #f8fafc !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.org-selector-container {
  padding: 0 24px;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px;
  }
  margin-bottom: 25px;
  padding: 15px;
  background-color: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  label {
    font-weight: 600;
    color: #2c3e50;
    min-width: 150px;
  }

  .org-selector {
    flex: 1;
    max-width: 500px;

    :deep(.el-input__wrapper) {
      box-shadow: 0 0 0 1px #dcdfe6 inset;
      padding: 1px 30px 1px 0;
    }

    :deep(.el-input__inner) {
      padding-left: 0;
      margin-left: 0;
      text-indent: 0;
      transition: all 0.2s;

      &:hover, &:focus {
        border-color: #005C6B;
      }
    }

    :deep(.el-select__input) {
      margin-left: 0;
      padding-left: 0;
    }

    :deep(.el-select-v2__placeholder),
    :deep(.el-select__placeholder) {
      padding-left: 0;
    }

    :deep(.el-select__tags) {
      margin-left: 0;
      padding-left: 0;
    }
  }

  :deep(.el-select-dropdown__item) {
    padding-left: 16px !important;

    &.selected {
      color: #005C6B;
      font-weight: 500;
    }

    &:hover {
      background-color: #f0f9ff;
    }
  }
}

.profile-container {
  max-width: 100%;
  margin: 0;
  padding: 0;
  padding-top: 24px;
  padding-bottom: 64px;
  text-align: left;
  
  @media (min-width: 768px) {
    padding-top: 32px;
    padding-bottom: 80px;
  }
  
  @media (min-width: 1024px) {
    padding-top: 40px;
    padding-bottom: 96px;
  }

  /* Add padding for embedded views */
  &.embedded {
    padding: 30px;

    @media (max-width: 768px) {
      padding: 20px;
    }
  }
}

.profile-content-container {
  margin-top: 20px;
  text-align: left;
  padding: 0 24px;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px;
  }
}

.profile-header {
  height: 300px;
  background-size: cover;
  background-position: center;
  position: relative;
  border-radius: 0 0 20px 20px;
  margin: 0 0 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  padding: 40px 24px;

  @media (min-width: 768px) {
    padding: 40px 40px;
  }

  @media (min-width: 1024px) {
    padding: 40px 64px;
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    height: 280px;
    padding: 15px 24px;
  }
}

.profile-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 20px;
  z-index: 1;
}

.profile-content-overlay {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 30px;
  width: 100%;
  padding-top: 100px;

  /* Mobile responsive - column layout */
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    text-align: center;
    padding-top: 0;
    gap: 20px;
  }
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid white;
  overflow: hidden;
  background: #fff;
  box-shadow: none;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    order: 1;
  }
}

.profile-name-section {
  color: white;
  text-align: left;

  /* Mobile responsive - center text */
  @media (max-width: 768px) {
    text-align: center;
    order: 2;
  }

  .user-name {
    color: white;
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 10px 0;
    letter-spacing: -0.5px;

    /* Mobile responsive */
    @media (max-width: 768px) {
      font-size: 24px;
    }
  }

  .user-title {
    color: rgba(255, 255, 255, 0.9);
    font-size: 16px;
    margin: 0;
    font-weight: 500;

    /* Mobile responsive */
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
}

.tabs-container {
  margin-bottom: 30px;
  text-align: left;
  padding: 0;
}

.tab-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  position: relative;

  /* Mobile layout - stack vertically */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
}

.tab-navigation {
  display: flex;
  align-items: center;
}

.search-container {
  display: flex;
  align-items: center;

  .sticker-search {
    width: 280px;

    :deep(.el-input__wrapper) {
      box-shadow: 0 0 0 1px #dcdfe6 inset;
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 0 0 1px #005C6B inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px #1fbeaf inset;
      }
    }

    :deep(.el-input__inner) {
      font-size: 14px;

      &::placeholder {
        color: #a8a8a8;
      }
    }

    :deep(.el-input__prefix-inner) {
      color: #a8a8a8;
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
      width: 100%;
      max-width: 350px;
    }
  }
}

.tab-button {
  background: none;
  border: none;
  padding: 12px 0;
  margin-right: 24px;
  font-size: 14px;
  font-weight: 800;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  text-transform: uppercase;

  /* Custom indicator that matches text width */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background-color: transparent;
    transition: background-color 0.3s ease;
  }

  &:hover {
    color: #1fbeaf;
  }

  &.active {
    color: #000000;

    &::after {
      background-color: #1fbeaf;
    }
  }
}

.tab-content {
  animation: fadeIn 0.3s ease-in;
  padding: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.profile-info-section {
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;

  h3 {
    color: #2c3e50;
    font-size: 14px;
    margin: 35px 0 20px;
    text-align: left;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      margin-top: 0;
    }
  }
}

.bio-section {
  text-align: left;
  margin: 30px 0;
  padding: 0;
  background: transparent;
  border-radius: 0;

  &:first-child {
    margin-top: 0;
  }

  p {
    color: #4a5568;
    line-height: 1.8;
    margin: 0;
    font-size: 18px;
  }
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 15px 0;
  padding: 5px;
}

.value-tag, .interest-tag, .channel-tag {
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 8px;
  transition: all 0.3s ease;
  background-color: transparent !important;
  color: #000000 !important;
  border: 1px solid #000000 !important;

  &:hover {
    transform: scale(1.05);
    background-color: #f5f5f5 !important;
  }
}

.values-section {
  text-align: left;
  margin: 30px 0;

  &:first-child {
    margin-top: 0;
  }
}

.contact-section {
  text-align: left;
  margin: 30px 0;

  &:first-child {
    margin-top: 0;
  }

  h3 {
    color: #2c3e50;
    font-size: 14px;
    margin: 35px 0 20px;
    text-align: left;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      margin-top: 0;
    }
  }

  .contact-info {
    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 10px 0;

      i {
        color: #666;
      }

      span {
        color: #4a5568;
        font-size: 16px;
      }
    }
  }
}

.stickers-section {
  text-align: left;
  margin-top: 0;

  h3 {
    margin-top: 0;
  }
}

.stickers-container {
  margin-top: 15px;
}

.sticker-search-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 15px;

  p {
    color: #5c6c7c;
    font-size: 16px;
    margin: 0;
  }

  .loading-spinner {
    color: #1fbeaf;
  }
}

.no-search-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 40px 20px;

  p {
    color: #6b7280;
    font-size: 16px;
    margin: 0;
    text-align: center;
    font-style: italic;
  }
}

.loading-spinner {
  animation: rotate 2s linear infinite;
  color: #1fbeaf;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 20px;
  padding: 0 24px;

  @media (min-width: 768px) {
    padding: 0 40px;
  }

  @media (min-width: 1024px) {
    padding: 0 64px;
  }

  p {
    color: #5c6c7c;
    font-size: 18px;
  }

  &.initial-loading {
    min-height: 50vh;
    background-color: #f8fafc;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);

    .loading-spinner {
      color: #005C6B;
    }

    p {
      font-size: 20px;
      font-weight: 500;
    }
  }
}

.auth-redirect {
  text-align: center;
  padding: 60px 24px;
  font-size: 20px;
  color: #5c6c7c;
  background: #f8fafc;
  border-radius: 12px;
  margin: 20px 24px;

  @media (min-width: 768px) {
    padding: 60px 40px;
    margin: 20px 40px;
  }

  @media (min-width: 1024px) {
    padding: 60px 64px;
    margin: 20px 64px;
  }
}

.error-message {
  background-color: #fef2f2;
  color: #ef4444;
  padding: 20px 24px;
  border-radius: 12px;
  margin: 0 24px 25px;
  text-align: center;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.1);

  @media (min-width: 768px) {
    padding: 20px 40px;
    margin: 0 40px 25px;
  }

  @media (min-width: 1024px) {
    padding: 20px 64px;
    margin: 0 64px 25px;
  }
}

/* Purpose title styling */
.purpose-title {
  color: #2c3e50;
  font-size: 24px;
  margin: 0 0 20px 0;
  text-align: left;
  font-weight: 700;
  line-height: 1.3;
}

/* Embedded view styles */
.embedded-profile-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 0;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
}

.embedded-profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  background: #fff;
  border: 2px solid #e5e7eb;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.embedded-profile-name-section {
  .embedded-user-name {
    color: #2c3e50;
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 5px 0;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  }

  .embedded-user-title {
    color: #6b7280;
    font-size: 14px;
    margin: 0;
    font-weight: 500;
  }
}

.embedded-shop-content {
  .profile-info-section {
    padding: 0;
  }

  .stickers-section {
    margin-top: 0;

    h3 {
      margin-top: 0;
    }
  }
}
</style>
